import { BehaviorSubject, EmptyError, firstValueFrom, ReplaySubject, Subject, throwError, TimeoutError, timer, } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, timeout } from 'rxjs/operators';
import { hexString, paddedHex } from '../util/misc.js';
import { timeoutRace } from '../util/sched.js';
import { BleDeviceError } from './interface.js';
import { pupAppHashChar, pupAppVersionChar, pupBootloaderHashChar, pupBootloaderVersionChar, pupDeviceInfoChar, pupGeneralCommandChar, pupGeneralCommandResponseChar, pupSerialNumberChar, pupService, pupTriggerChar, silabsOtaAppVersionChar, silabsOtaControlChar, silabsOtaDataChar, silabsOtaLoaderVersionChar, silabsOtaService, silabsOuis, } from './uuids.js';
import { revisionStringToNumber } from './version-string.js';
const errorMessages = {
    cancelled: 'User cancelled',
    notConnected: 'Not connected to a device',
    timeout: 'Timeout',
    didNotEnterLoader: 'Did not enter loader',
    flashNotIdle: 'Flash already in progress',
    flashFailed: 'Flash failed',
    notInApp: 'Device is not in the application',
    notInBootloader: 'Device is not in the bootloader',
    internalError: 'Internal error',
    badEuid: 'Invalid EUID',
    unsupportedBootloader: 'Unsupported bootloader',
};
export class OtaError extends Error {
    cause;
    detail;
    static causeFromAny(err) {
        return err instanceof OtaError ? err.cause : undefined;
    }
    constructor(cause, detail = {}) {
        super(errorMessages[cause]);
        this.cause = cause;
        this.detail = detail;
    }
}
const writeWithResponsePeriod = 64;
const toThrow = (errFactory) => (err) => {
    throw errFactory(err);
};
const cancelRace = (p, cancel) => Promise.race([p, firstValueFrom(cancel.pipe(mergeMap((v) => throwError(() => v))))]);
const pupRebootTimeout = 200;
export const isAppDeviceState = (state) => state === 'siApp' || state === 'pupApp';
export const isLoaderDeviceState = (state) => state === 'siLoader' || state === 'pupLoader';
export const isConnectedDeviceState = (state) => state !== 'offline';
export const isPupDeviceState = (state) => state === 'pupApp' || state === 'pupLoader';
export const isSilabsDeviceState = (state) => state === 'siApp' || state === 'siLoader';
const getWriter = async (conn, writeWithResponse, svc, char) => {
    if (writeWithResponse || !(await conn.canWriteWithoutResponse(svc, char))) {
        return (d, _forceResponse) => conn.writeCharWithResponse(svc, char, d);
    }
    else {
        return (d, forceResponse) => forceResponse
            ? conn.writeCharWithResponse(svc, char, d)
            : conn.writeCharWithoutResponse(svc, char, d);
    }
};
class PupConnection {
    conn;
    cancelRaceInner;
    logger;
    writeWithResponse;
    runCommandInner;
    replySub;
    constructor(conn, cancelRaceInner, logger, writeWithResponse) {
        this.conn = conn;
        this.cancelRaceInner = cancelRaceInner;
        this.logger = logger;
        this.writeWithResponse = writeWithResponse;
    }
    async setup() {
        if (this.writeWithResponse) {
            this.runCommandInner = async (command, replySize, timeoutMsec, _replyFilter) => {
                await this.conn.writeCharWithResponse(pupService, pupGeneralCommandResponseChar, command);
                const respPromise = this.conn.readChar(pupService, pupGeneralCommandResponseChar);
                const resp = await timeoutRace(respPromise, timeoutMsec);
                const commandCode = new DataView(command).getUint8(0);
                const view = new DataView(resp);
                if (view.byteLength !== replySize || view.getUint8(0) !== commandCode) {
                    throw new OtaError('flashFailed', {
                        response: hexString(resp),
                        command: hexString(command),
                        message: 'bad response',
                    });
                }
                return view;
            };
        }
        else {
            const subj = new Subject();
            const obs = await this.cancelRace(this.conn.observeNotifications(pupService, pupGeneralCommandChar));
            this.replySub = obs.subscribe(subj);
            try {
                const writeCommand = await this.cancelRace(getWriter(this.conn, this.writeWithResponse, pupService, pupGeneralCommandChar));
                this.runCommandInner = async (command, replySize, timeoutMsec, replyFilter) => {
                    const commandCode = new DataView(command).getUint8(0);
                    // Buffer one reply, in case it arrives before we get a chance to subscribe to it
                    const replay = new ReplaySubject(1);
                    const replaySub = subj.subscribe(replay);
                    try {
                        await writeCommand(command, false);
                        const view = await firstValueFrom(replay.pipe(map((resp) => new DataView(resp)), filter((d) => {
                            if (d.getUint8(0) === commandCode &&
                                d.byteLength === replySize &&
                                replyFilter(d)) {
                                return true;
                            }
                            this.logger.info('filtered PUP reply', {
                                value: hexString(d.buffer),
                            });
                            return false;
                        }), timeout(timeoutMsec)));
                        return view;
                    }
                    catch (error) {
                        if (error instanceof EmptyError) {
                            throw new OtaError('notConnected', {
                                message: 'no response (connection closed?)',
                            });
                        }
                        throw error;
                    }
                    finally {
                        replaySub.unsubscribe();
                    }
                };
            }
            catch (err) {
                this.replySub?.unsubscribe();
                this.replySub = undefined;
                throw err;
            }
        }
    }
    async runCommand(command, replySize, timeoutMsec, replyFilter, retries = 10) {
        if (!this.runCommandInner) {
            throw new OtaError('internalError', { message: 'PupConnection is not set up' });
        }
        // Retry commands if they time out. Bootloader < AE doesn't support re-sending write commands,
        // but there's a chance the command never made it to the bootloader, and a failure is otherwise
        // fatal, so there's no downside to retrying.
        for (let i = 0; i < retries; ++i) {
            try {
                return await this.runCommandInner(command, replySize, timeoutMsec, replyFilter);
            }
            catch (err) {
                if (err !== 'timeout' && !(err instanceof TimeoutError)) {
                    throw err;
                }
            }
        }
        throw new OtaError('timeout', { command: hexString(command) });
    }
    teardown() {
        this.replySub?.unsubscribe();
        this.replySub = undefined;
        this.runCommandInner = undefined;
    }
    cancelRace(p) {
        return this.cancelRaceInner(p);
    }
}
export class OtaManager {
    conn;
    logger;
    /** Application version number, if known */
    appVersionNumber;
    /** Application git hash, if known */
    appGitVersion;
    /** Bootloader version number, if known */
    blVersionNumber;
    /** Bootloader git hash, if known */
    blGitVersion;
    /** Device serial number, if known */
    serialNumber;
    /** Maximum throughput to allow with SiLabs bootloaders */
    silabsMaxBytesPerSecond = 4000; // empirically determined
    /** Maximum block size to use with SiLabs bootloaders */
    silabsBlockSize = 20;
    /** Force write-with-response on all transactions? */
    writeWithResponse = false;
    deviceStateSubj = new BehaviorSubject('offline');
    flashStateSubj = new BehaviorSubject('idle');
    flashProgressSubj = new BehaviorSubject(undefined);
    cancelSubj = new Subject();
    param;
    subscriptions = [];
    /**
     * @param conn `ConnectionManager` instance for the connection
     * @param logger `Logger` to use for errors
     */
    constructor(conn, logger) {
        this.conn = conn;
        this.logger = logger;
        this.subscriptions.push(this.conn.deviceStateObs
            .pipe(filter((state) => state !== 'connected'))
            .subscribe(this.clearDeviceState));
        this.subscriptions.push(this.deviceStateSubj.subscribe((ds) => this.logger.log(`OTA device state: ${ds}`)));
        this.subscriptions.push(this.flashStateSubj.subscribe((fs) => this.logger.log(`OTA flash state: ${fs}`)));
    }
    /** Connect the ParamRegistry once it's constructed */
    setup(params) {
        if (this.param) {
            throw new Error('Multiple calls to OtaManager#setup');
        }
        this.param = params;
    }
    /** Close all subscriptions */
    teardown() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
    /** State of the connected device */
    get deviceState() {
        return this.deviceStateSubj.value;
    }
    /** `Observable` of the state of the connected device */
    get deviceStateObs() {
        return this.deviceStateSubj.asObservable();
    }
    /** True if a flash operation is ongoing */
    get isBusy() {
        return this.flashStateSubj.value !== 'idle';
    }
    /** `Observable` emitting `true` if a flash operation is ongoing */
    get isBusyObs() {
        return this.flashStateSubj.pipe(map((v) => v !== 'idle'));
    }
    /** State of any ongoing flash operation */
    get flashState() {
        return this.flashStateSubj.value;
    }
    /** `Observable` of the state of any ongoing flash operation */
    get flashStateObs() {
        return this.flashStateSubj.asObservable();
    }
    /** Progress percentage of any ongoing flash operation */
    get flashProgress() {
        return this.flashProgressSubj.value;
    }
    /** `Observable` of the progress percentage of any ongoing flash operation */
    get flashProgressObs() {
        return this.flashProgressSubj.asObservable();
    }
    /** `true` if a device is connected and its state has been determined */
    get isConnected() {
        return isConnectedDeviceState(this.deviceStateSubj.value);
    }
    /** `Observable` emitting `true` if a device is connected and its state has been determined */
    get isConnectedObs() {
        return this.deviceStateSubj.pipe(map(isConnectedDeviceState), distinctUntilChanged());
    }
    /** `true` if a device is connected and it is running the application */
    get isConnectedApp() {
        return isAppDeviceState(this.deviceStateSubj.value);
    }
    /** `Observable` emitting `true` if a device is connected and it is running the application */
    get isConnectedAppObs() {
        return this.deviceStateSubj.pipe(map(isAppDeviceState), distinctUntilChanged());
    }
    /** `true` if a device is connected and it is running the bootloader/AppLoader */
    get isConnectedLoader() {
        return isLoaderDeviceState(this.deviceStateSubj.value);
    }
    /** `Observable` emitting `true` if a device is connected and it is running the bootloader/AppLoader */
    get isConnectedLoaderObs() {
        return this.deviceStateSubj.pipe(map(isLoaderDeviceState), distinctUntilChanged());
    }
    /** `true` if a device is connected and it has a PUP bootloader */
    get isConnectedPup() {
        return isPupDeviceState(this.deviceStateSubj.value);
    }
    /** `Observable` emitting `true` if a device is connected and it has a PUP bootloader */
    get isConnectedPupObs() {
        return this.deviceStateSubj.pipe(map(isPupDeviceState), distinctUntilChanged());
    }
    /** `true` if a device is connected and it has a SiLabs bootloader */
    get isConnectedSilabs() {
        return isSilabsDeviceState(this.deviceStateSubj.value);
    }
    /** `Observable` emitting `true` if a device is connected and it has a SiLabs bootloader */
    get isConnectedSilabsObs() {
        return this.deviceStateSubj.pipe(map(isSilabsDeviceState), distinctUntilChanged());
    }
    /** Cancel an ongoing `enterBootloader` or `flash` */
    cancel() {
        this.cancelSubj.next(new OtaError('cancelled'));
    }
    /**
     * @brief Tell a connected device running the application to enter the bootloader.
     * @param validateEuid If the EUID is not 6 bytes, or if it's a SiLabs device and doesn't match
     * the SiLabs prefix list, throw an error instead of entering the bootloader.
     * @returns The device name that will be used by the bootloader, derived from the EUID
     */
    async enterBootloader(validateEuid = true) {
        try {
            if (this.flashState !== 'idle') {
                throw new OtaError('flashNotIdle');
            }
            if (!this.isConnected) {
                throw new OtaError('notConnected');
            }
            if (!this.isConnectedApp) {
                throw new OtaError('notInApp');
            }
            const otaName = await this.cancelRace(this.getOtaNameFromEuid(validateEuid));
            if (this.deviceState === 'siApp' && validateEuid) {
                // validate against OUI list, since we can't find SiLabs devices by their advertised service
                if (!silabsOuis.some((oui) => otaName.startsWith(oui))) {
                    throw new OtaError('badEuid', {
                        euid: otaName,
                        message: 'EUID does not begin with a known SiLabs OUI',
                    });
                }
            }
            this.logger.log(`Initiating reboot to AppLoader`);
            await this.cancelRace(this.deviceState === 'siApp' ? this.writeControl('start') : this.writeTrigger());
            // wait for device to kick us offline
            await this.cancelRace(this.awaitDisconnect());
            return otaName;
        }
        catch (error) {
            if (OtaError.causeFromAny(error) !== 'cancelled') {
                this.logger.log(`enterBootloader failed`, { error });
            }
            throw error;
        }
    }
    /**
     * Tell a connected device running the bootloader to reboot into the application.
     * If successful, ends with device disconnected and reboot in progress.
     */
    async exitBootloader() {
        if (this.flashState !== 'idle') {
            throw new OtaError('flashNotIdle');
        }
        if (!this.isConnected) {
            throw new OtaError('notConnected');
        }
        if (!this.isConnectedLoader) {
            throw new OtaError('notInBootloader');
        }
        try {
            this.logger.log(`Initiating reboot to application`);
            if (this.deviceState === 'siLoader') {
                await this.rebootSilabs();
            }
            else {
                await this.rebootPup();
            }
            // wait for disconnection to finish
            await this.cancelRace(this.awaitDisconnect());
        }
        catch (error) {
            if (OtaError.causeFromAny(error) !== 'cancelled') {
                this.logger.log(`exitBootloader failed`, { error });
            }
            throw error;
        }
        finally {
            this.flashProgressSubj.next(undefined);
            this.flashStateSubj.next('idle');
        }
    }
    /**
     * Program a FW image to a connected device running the bootloader.
     * If successful, ends with the device rebooting into the new application.
     */
    async flash(data) {
        if (this.flashState !== 'idle') {
            throw new OtaError('flashNotIdle');
        }
        if (!this.isConnected) {
            throw new OtaError('notConnected');
        }
        if (!this.isConnectedLoader) {
            throw new OtaError('notInBootloader');
        }
        try {
            if (this.deviceState === 'siLoader') {
                await this.programSilabs(data);
            }
            else {
                await this.programPup(data);
            }
        }
        catch (error) {
            if (OtaError.causeFromAny(error) !== 'cancelled') {
                this.logger.log(`flash failed`, { error });
            }
            throw error;
        }
        finally {
            this.flashProgressSubj.next(undefined);
            this.flashStateSubj.next('idle');
        }
    }
    /**
     * Wait up to 10000ms (default) for the `ConnectionManager` to indicate disconnected,
     * throwing an `OtaError` with `cause` of `timeout` if time expires
     */
    awaitDisconnect(timeoutMsec = 10000) {
        return firstValueFrom(this.conn.deviceStateObs.pipe(filter((v) => v === 'disconnected'), timeout({
            each: timeoutMsec,
            with: () => throwError(() => new OtaError('timeout', { message: 'awaiting disconnect' })),
        })));
    }
    /**
     * Check whether the device is in the application or bootloader, and which bootloader protocol it uses.
     * Also reads params that don't require unlock.
     * @returns Value of `deviceState`
     */
    async checkDeviceState() {
        try {
            const state = await this.getDeviceState();
            this.appVersionNumber = undefined;
            this.appGitVersion = undefined;
            this.blVersionNumber = undefined;
            this.blGitVersion = undefined;
            this.serialNumber = undefined;
            if (state === 'offline') {
                this.deviceStateSubj.next('offline');
                throw new Error('Connection dropped early in checkDeviceStateInner');
            }
            if (isSilabsDeviceState(state)) {
                this.appVersionNumber = await this.readUint32Char(silabsOtaService, silabsOtaAppVersionChar, 'SiLabs OTA app version number');
            }
            else if (isPupDeviceState(state)) {
                this.appVersionNumber = await this.readUint32Char(pupService, pupAppVersionChar, 'PUP OTA app version number');
                if (state === 'pupLoader' || this.isLorax) {
                    await this.readPupFlatLockedParams();
                }
                // Bootloader version chars are only present in bootloader, and only versions AG and higher
                // Swallow missing-char errors
                this.blVersionNumber = await this.readUint32Char(pupService, pupBootloaderVersionChar, 'PUP bootloader version number', false);
                this.blGitVersion = await this.readStringChar(pupService, pupBootloaderHashChar, 'PUP bootloader commit hash', false);
            }
            if (state === 'siApp' && !this.isLorax) {
                this.appGitVersion = await this.readParam('gitHash');
            }
            this.deviceStateSubj.next(state);
            return state;
        }
        catch (error) {
            const msgObj = {
                error,
                connDeviceState: this.conn.deviceState,
            };
            if (this.conn.deviceState !== 'connected') {
                this.logger.warn('Connection dropped during checkDeviceState()', msgObj);
            }
            else {
                this.logger.error('checkDeviceState() failed', msgObj);
            }
            throw error;
        }
    }
    /**
     * Read params that require unlock. These are serial number and app version hash on flat service PUP devices,
     * and app version hash on Lorax SiLabs devices. This should be called after unlock is complete.
     */
    async readUnlockedParams() {
        try {
            if (this.deviceState === 'pupApp' && !this.isLorax) {
                // Flat service PUP devices require unlock to read some params; do it now
                await this.readPupFlatLockedParams();
            }
            if (this.deviceState === 'siApp' && this.isLorax) {
                // Lorax Pikachus require unlock to read git hash from a regular param
                this.appGitVersion = await this.readParam('gitHash');
            }
        }
        catch (error) {
            const msgObj = {
                error,
                connDeviceState: this.conn.deviceState,
            };
            if (this.conn.deviceState !== 'connected') {
                this.logger.warn('Connection dropped during onUnlockedChanged()', msgObj);
            }
            else {
                this.logger.error('onUnlockedChanged() failed', msgObj);
            }
            throw error;
        }
    }
    getDeviceState = async () => {
        if (this.conn.deviceState !== 'connected') {
            return 'offline';
        }
        try {
            // The AppLoader binary provided by SiLabs will lock up and reboot if the host attempts to read
            // a characteristic that does not exist in the AppLoader GATT database. Therefore, determine
            // whether Pikachu is in the loader by attempting to read the AppLoader version characteristic,
            // and if it exists, do not attempt to read any of the chars defined by the main application.
            const ab = await this.conn.readChar(silabsOtaService, silabsOtaLoaderVersionChar);
            this.logger.info('Read SiLabs OTA loader version', { ab: new Uint8Array(ab) });
            if (ab.byteLength === 4 && new DataView(ab).getUint32(0, true) === 0x66667550) {
                return 'pupLoader';
            }
            return 'siLoader';
        }
        catch (error) {
            this.logger.info('Failed to read SiLabs OTA loader version', { error });
            const cause = BleDeviceError.causeFromAny(error);
            if (cause === 'notConnected' || cause === 'notBonded') {
                return 'offline';
            }
            if (cause !== 'charNotFound') {
                throw error;
            }
        }
        try {
            await this.conn.readChar(silabsOtaService, silabsOtaAppVersionChar);
            this.logger.info('Read SiLabs OTA app version');
            return 'siApp';
        }
        catch (error) {
            this.logger.info('Failed to read SiLabs OTA app version', { error });
            const cause = BleDeviceError.causeFromAny(error);
            if (cause === 'notConnected' || cause === 'notBonded') {
                return 'offline';
            }
            if (cause !== 'charNotFound') {
                throw error;
            }
        }
        try {
            await this.conn.readChar(pupService, pupAppVersionChar);
            this.logger.info('Read PUP app version');
            return 'pupApp';
        }
        catch (error) {
            this.logger.warn('Failed to read PUP app version', { error });
            return 'offline';
        }
    };
    clearDeviceState = () => {
        this.appVersionNumber = undefined;
        this.appGitVersion = undefined;
        this.blVersionNumber = undefined;
        this.blGitVersion = undefined;
        this.serialNumber = undefined;
        this.deviceStateSubj.next('offline');
    };
    async readUint32Char(svc, char, desc, warnOnMissing = true) {
        try {
            const buffer = await this.conn.readChar(svc, char);
            const view = new DataView(buffer);
            return view.getUint32(0, true);
        }
        catch (error) {
            if (warnOnMissing || BleDeviceError.causeFromAny(error) !== 'charNotFound') {
                this.logger.warn(`Failed to read ${desc} char`, { error });
            }
            return undefined;
        }
    }
    async readStringChar(svc, char, desc, warnOnMissing = true) {
        try {
            const buffer = await this.conn.readChar(svc, char);
            const decoder = new TextDecoder();
            return decoder.decode(buffer);
        }
        catch (error) {
            if (warnOnMissing || BleDeviceError.causeFromAny(error) !== 'charNotFound') {
                this.logger.warn(`Failed to read ${desc} char`, { error });
            }
            return undefined;
        }
    }
    /**
     * Write a characteristic that will immediately trigger a disconnect from the target device
     * (server) side. Both Atmosic and SiLabs BLE stacks appear to have race conditions that
     * sometimes prevent the write acknowledgement from making it out before the connection drops.
     * @param svc Service UUID
     * @param char Characteristic UUID
     * @param value Value to write
     */
    writeDisconnectingChar = async (svc, char, value) => {
        try {
            await this.conn.writeCharWithResponse(svc, char, value);
        }
        catch (error) {
            // `notConnected` may occur on Path and Android app, `gatt` on Mac Chrome
            const expectedErrors = ['notConnected', 'gatt'];
            if (expectedErrors.includes(BleDeviceError.causeFromAny(error))) {
                this.logger.warn('writeDisconnectingChar() ack failed', { error });
            }
            else {
                this.logger.error('writeDisconnectingChar() unexpected error', { error });
                throw error;
            }
        }
    };
    /** Write SiLabs OTA control characteristic */
    writeControl = async (command) => {
        const view = new DataView(new ArrayBuffer(1));
        view.setUint8(0, command === 'start' ? 0x00 : command === 'end' ? 0x03 : 0x04);
        if (command === 'start' && this.deviceState === 'siLoader') {
            // This doesn't trigger a disconnect, so just write with response
            await this.conn.writeCharWithResponse(silabsOtaService, silabsOtaControlChar, view);
        }
        else {
            await this.writeDisconnectingChar(silabsOtaService, silabsOtaControlChar, view);
        }
    };
    /** Write PUP trigger characteristic; this is only used when connected to app */
    writeTrigger = async () => {
        const zero = new Uint8Array([0]);
        await this.writeDisconnectingChar(pupService, pupTriggerChar, zero.buffer);
    };
    readParam = async (key) => {
        if (!this.param) {
            throw new OtaError('internalError', { message: 'OtaManager#param not set' });
        }
        const fails = [];
        for (const param of this.param.get(key)) {
            try {
                return await param.readOnce();
            }
            catch (err) {
                fails.push({ param, err });
            }
        }
        console.log(`Failed to read ${key}`, fails);
    };
    getOtaNameFromEuid = async (validateEuid) => {
        const euidLength = 6;
        const euid = await this.readParam('euid');
        if (!euid) {
            throw new OtaError('badEuid', { message: 'Could not read EUID param' });
        }
        const bytes = Array.from(new Uint8Array(euid));
        bytes.reverse();
        const hexStr = bytes
            .map((b) => paddedHex(b, 2))
            .join('')
            .toUpperCase();
        // Check length
        if (validateEuid && bytes.length !== euidLength) {
            throw new OtaError('badEuid', {
                euid: hexStr,
                expectedLength: euidLength,
                message: 'EUID length does not match',
            });
        }
        return hexStr;
    };
    cancelRace = (p) => cancelRace(p, this.cancelSubj);
    get isLorax() {
        return (this.appVersionNumber ?? 0) >= revisionStringToNumber('AB');
    }
    /**
     * Read params that require unlock on PUP devices with flat service
     */
    async readPupFlatLockedParams() {
        this.serialNumber = await this.readStringChar(pupService, pupSerialNumberChar, 'serial number');
        this.appGitVersion = await this.readStringChar(pupService, pupAppHashChar, 'PUP app commit hash');
    }
    getWriter(svc, char) {
        return getWriter(this.conn, this.writeWithResponse, svc, char);
    }
    async programSilabs(data) {
        // device is in the AppLoader
        this.flashStateSubj.next('startOta');
        this.logger.log(`Starting OTA`);
        await this.cancelRace(this.writeControl('start'));
        const writeData = await this.cancelRace(this.getWriter(silabsOtaService, silabsOtaDataChar));
        // download the GBL
        this.flashStateSubj.next('download');
        this.logger.log(`Downloading OTA image`);
        for (let i = 0; i < data.byteLength; i += this.silabsBlockSize) {
            const sliceEnd = Math.min(i + this.silabsBlockSize, data.byteLength);
            const minTime = (this.silabsBlockSize * 1000) / this.silabsMaxBytesPerSecond;
            // do a write-with-response periodically, forcing the whole queue to be processed on Windows
            const packetNum = i / this.silabsBlockSize;
            const forceResponse = packetNum % writeWithResponsePeriod == 0;
            await this.cancelRace(Promise.all([
                firstValueFrom(timer(minTime)),
                writeData(data.slice(i, sliceEnd), forceResponse),
            ]));
            this.flashProgressSubj.next((sliceEnd / data.byteLength) * 100);
        }
        this.logger.log(`Committing OTA`);
        this.flashStateSubj.next('finish');
        await this.cancelRace(this.writeControl('end').catch(toThrow((error) => new OtaError('flashFailed', { error }))));
        this.logger.log(`OTA finished`);
    }
    async rebootSilabs() {
        await this.cancelRace(this.writeControl('reboot'));
    }
    async programPup(data) {
        this.flashStateSubj.next('startOta');
        this.logger.log(`Starting OTA`);
        const deviceInfoAb = await this.cancelRace(this.conn.readChar(pupService, pupDeviceInfoChar));
        const deviceInfoView = new DataView(deviceInfoAb);
        const bootloaderApi = deviceInfoView.getUint8(0);
        if (bootloaderApi !== 0) {
            throw new OtaError('unsupportedBootloader', {
                bootloaderApi,
                message: 'Unsupported bootloader API',
            });
        }
        const maxCommandSize = deviceInfoView.getUint8(1);
        const chunkSize = maxCommandSize - 5;
        if (chunkSize < 1) {
            throw new OtaError('unsupportedBootloader', {
                maxCommandSize,
                message: 'Max command size is too small',
            });
        }
        const writeTimeout = deviceInfoView.getUint16(12, true) * 10;
        const verifyTimeout = deviceInfoView.getUint16(14, true) * 10;
        this.flashStateSubj.next('download');
        this.logger.log(`Downloading PUFF`);
        const pupConn = new PupConnection(this.conn, this.cancelRace, this.logger, this.writeWithResponse);
        await pupConn.setup();
        try {
            for (let i = 0; i < data.byteLength; i += chunkSize) {
                // write chunks of the file
                const chunkEnd = Math.min(i + chunkSize, data.byteLength);
                const chunk = data.slice(i, chunkEnd);
                const command = new Uint8Array(5 + chunk.byteLength);
                // opcode 0: write
                command[0] = 0;
                // offset
                new DataView(command.buffer).setUint32(1, i, true);
                // payload
                command.set(new Uint8Array(chunk), 5);
                try {
                    const resp = await this.cancelRace(pupConn.runCommand(command.buffer, 6, writeTimeout, (d) => d.getUint32(1, true) >= i));
                    const offset = resp.getUint32(1, true);
                    const error = resp.getUint8(5);
                    if (offset !== i) {
                        throw new OtaError('flashFailed', {
                            offset,
                            command: hexString(command),
                            message: 'unexpected offset',
                        });
                    }
                    if (error) {
                        throw new OtaError('flashFailed', {
                            offset,
                            command: hexString(command),
                            errorCode: error,
                        });
                    }
                }
                catch (error) {
                    this.logger.error('Program pup failed.', { error });
                    if (error instanceof OtaError) {
                        // Rethrow the error, adding failure address if needed
                        if (!('offset' in error.detail)) {
                            error.detail.offset = i;
                        }
                        throw error;
                    }
                    throw new OtaError('flashFailed', { error });
                }
                this.flashProgressSubj.next((chunkEnd / data.byteLength) * 100);
            }
            this.logger.log(`Committing OTA`);
            this.flashStateSubj.next('finish');
            try {
                // opcode 1: verify checksum
                const verifyCommand = new Uint8Array([1]).buffer;
                const resp = await this.cancelRace(pupConn.runCommand(verifyCommand, 2, verifyTimeout, () => true));
                const error = resp.getUint8(1);
                if (error) {
                    throw new OtaError('flashFailed', { errorCode: error });
                }
            }
            catch (error) {
                this.logger.log('Verify pup failed.', { error });
                if (error instanceof OtaError) {
                    // Rethrow the error, adding failure operation if needed
                    if (!('operation' in error.detail)) {
                        error.detail.operation = 'verifyChecksum';
                    }
                    throw error;
                }
                throw new OtaError('flashFailed', { error });
            }
            await this.rebootPupInner(pupConn);
            this.logger.log(`OTA finished`);
        }
        finally {
            pupConn.teardown();
        }
    }
    async rebootPup() {
        const pupConn = new PupConnection(this.conn, this.cancelRace, this.logger, this.writeWithResponse);
        await pupConn.setup();
        try {
            await this.rebootPupInner(pupConn);
        }
        finally {
            pupConn.teardown();
        }
        // reboot will now happen on disconnect
        await this.cancelRace(this.conn.disconnect());
    }
    async rebootPupInner(pupConn) {
        try {
            // opcode 2: reboot
            const rebootCommand = new Uint8Array([2]).buffer;
            const resp = await this.cancelRace(pupConn.runCommand(rebootCommand, 2, pupRebootTimeout, () => true));
            const error = resp.getUint8(1);
            if (error) {
                throw new OtaError('flashFailed', { errorCode: error });
            }
        }
        catch (error) {
            this.logger.log('Reboot pup failed.', { error });
            if (error instanceof OtaError) {
                // Rethrow the error, adding failure address if needed
                if (!('operation' in error.detail)) {
                    error.detail.operation = 'reboot';
                }
                throw error;
            }
            throw new OtaError('flashFailed', { error });
        }
    }
}
//# sourceMappingURL=ota-manager.js.map